import React, { FC } from 'react';
import { Box, Slider } from '@mui/material';
import ConfirmActionPopover from 'js/components/ConfirmPopovers/ConfirmActionPopover';
import { DrawingPopoverStatus } from '../DrawingPopoverStatus/DrawingPopoverStatus';
import { PolygonDrawingHelpIcon } from '../PolygonDrawingHelpIcon';
import {
  getPopoverTitle,
  getPopoverActionText,
  getPopoverMessage,
} from '../../ConfirmPopovers/PopoverUtils';
import areaCalculation from 'js/algorithms/geometry/AreaCalculation';
import { useLangFile } from '../../../context/LanguageContext';

interface Polygon {
  path: Array<{ lat: number; lng: number }>;
}

interface MagicWandState {
  isSelecting: boolean;
}

interface DrawingPopoverProps {
  popup: boolean;
  contextState: {
    polygons: Polygon[];
    drawingMode: string;
    changingThreshold: number;
  };
  props: {
    isDrawingNote?: boolean;
    isPlacingMarker?: boolean;
    isDrawingField?: boolean;
    isDrawingFieldHole?: boolean;
    isEditingFieldBoundaries?: boolean;
    isSplittingField?: boolean;
    isEditingMergedField?: boolean;
    magicWand: MagicWandState;
    [key: string]: any;
  };
  onSubmit: () => void;
  stopDrawing: () => void;
  handleThresholdChanged: (event: any, value: number | number[]) => void;
  shouldSubmitButtonDisable: () => {
    disable: boolean;
    reason?: string;
    fieldASize?: string;
    fieldBSize?: string;
  };
  dispatch: React.Dispatch<{ type: string; payload?: any }>;
}

const DrawingPopover: FC<DrawingPopoverProps> = ({
  popup,
  contextState,
  props,
  onSubmit,
  stopDrawing,
  handleThresholdChanged,
  shouldSubmitButtonDisable,
  dispatch,
}) => {
  if (!popup) return null;

  const LangFile = useLangFile();
  const { polygons, drawingMode, changingThreshold } = contextState;

  const submitButtonDisable = shouldSubmitButtonDisable();

  const hectaresToAdd = polygons
    ?.reduce((acc, polygon) => {
      const { size } = areaCalculation({ coords: polygon.path });
      return acc + size;
    }, 0)
    .toFixed(2);

  const status = (
    <DrawingPopoverStatus
      drawingMode={drawingMode}
      hectaresToAdd={hectaresToAdd}
      LangFile={LangFile}
      setSplitFieldInput={(key: string, value: any) =>
        dispatch({ type: 'SET_SPLIT_FIELD_INPUTS', payload: { key, value } })
      }
      setMergeFieldInput={(key: string, value: any) =>
        dispatch({ type: 'SET_MERGE_FIELD_INPUTS', payload: { key, value } })
      }
      fieldASize={submitButtonDisable.fieldASize}
      fieldBSize={submitButtonDisable.fieldBSize}
      {...contextState}
      {...props}
    />
  );

  const helpIcon = <PolygonDrawingHelpIcon drawingMode={drawingMode} />;

  return (
    <ConfirmActionPopover
      shown={
        props.isDrawingNote ||
        props.isPlacingMarker ||
        props.isDrawingField ||
        props.magicWand.isSelecting ||
        props.isDrawingFieldHole ||
        props.isEditingFieldBoundaries ||
        props.isSplittingField ||
        props.isEditingMergedField
      }
      title={getPopoverTitle(props)}
      message={getPopoverMessage(props)}
      style={{ marginTop: '30px' }}
      status={status}
      actionText={getPopoverActionText(props, drawingMode)}
      disabled={submitButtonDisable.disable}
      disabledReason={submitButtonDisable.reason}
      action={!props.isPlacingMarker ? onSubmit : undefined}
      cancelText={LangFile.PolygonDrawing.cancel}
      cancelAction={stopDrawing}
      helpIcon={helpIcon}
    >
      {props.magicWand.isSelecting && (
        <Box
          sx={{
            fontWeight: 300,
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{LangFile.PolygonDrawing.magicThresholdLabel}:</span> {changingThreshold}
          <Slider
            step={2}
            value={changingThreshold}
            min={1}
            max={50}
            onChange={handleThresholdChanged}
            style={{ padding: '10px 0' }}
          />
        </Box>
      )}
    </ConfirmActionPopover>
  );
};

export default DrawingPopover;
